import { Outlet, Link, NavLink } from "react-router-dom";
import { BookOpen, Map, Search } from "lucide-react";
import ThemeToggle from "@/app/ThemeToggle";
import HeaderSearch from "@/app/HeaderSearch";
import ConsentBanner from "@/app/ConsentBanner";
import { usePageTracking } from "@/lib/usePageTracking";

function navLinkClass({ isActive }: { isActive: boolean }) {
  return [
    "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm transition-colors",
    isActive
      ? "bg-brand-50 text-brand-700 dark:bg-slate-800 dark:text-brand-300"
      : "hover:bg-slate-100 dark:hover:bg-slate-800",
  ].join(" ");
}

export default function Layout() {
  usePageTracking();
  return (
    <div className="min-h-screen flex flex-col bg-warm-50 dark:bg-surface-950">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:text-slate-900 focus:px-3 focus:py-1 focus:rounded"
      >
        Skip to content
      </a>
      {/* Signature Cowork gradient bar (blue → purple → coral) */}
      <div
        aria-hidden
        className="h-1 w-full"
        style={{ background: "linear-gradient(90deg, #5a48d4 0%, #8b6ef0 40%, #d77a98 70%, #ec6e4c 100%)" }}
      />
      <header className="sticky top-0 z-30 backdrop-blur bg-white/85 dark:bg-surface-950/80 border-b border-slate-200 dark:border-surface-800">
        <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-semibold text-lg whitespace-nowrap"
            aria-label="Cowork Cookbook home"
          >
            <img
              src="/favicon.svg"
              alt=""
              aria-hidden
              width={32}
              height={32}
              className="w-8 h-8 rounded-lg shadow-sm"
            />
            <span className="hidden sm:inline">Cowork Cookbook</span>
          </Link>
          <HeaderSearch />
          <nav className="ml-auto flex items-center gap-1 text-sm">
            <NavLink to="/map" className={navLinkClass}>
              <Map size={16} aria-hidden /> Map
            </NavLink>
            <NavLink to="/recipes" className={navLinkClass}>
              <BookOpen size={16} aria-hidden /> Recipes
            </NavLink>
            <NavLink to="/search" className={navLinkClass} aria-label="Search">
              <Search size={16} aria-hidden />
              <span className="sr-only">Search</span>
            </NavLink>
            <ThemeToggle />
          </nav>
        </div>
      </header>
      <main id="main" className="flex-1 max-w-6xl mx-auto w-full px-4 py-8">
        <Outlet />
      </main>
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
        <div className="max-w-6xl mx-auto px-4 py-4 text-xs text-slate-500 dark:text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
          <span>
            Not affiliated with Microsoft. Microsoft 365 Copilot and Dynamics 365 are
            trademarks of Microsoft Corporation.
          </span>
          <nav className="ml-auto flex flex-wrap gap-3">
            <Link to="/about" className="hover:underline">
              About
            </Link>
            <Link to="/privacy" className="hover:underline">
              Privacy
            </Link>
            <Link to="/terms" className="hover:underline">
              Terms
            </Link>
            <Link to="/trademark" className="hover:underline">
              Trademark
            </Link>
            <Link to="/report" className="hover:underline">
              Report
            </Link>
            <a
              href="https://github.com/seangalliher/Coworkcookbook"
              target="_blank"
              rel="noreferrer"
              className="hover:underline"
            >
              GitHub
            </a>
          </nav>
        </div>
      </footer>
      <ConsentBanner />
    </div>
  );
}
